/**
 * features/hr/Severance.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — Severance & Final Settlement (one-off, on exit).
 *
 * Statutory severance (Botswana Employment Act §27, 76–78): 1 day's
 * basic pay per completed month for the first 60 months, 2 days' per
 * month beyond. Vests at 60 months and is NOT payable to gratuity/
 * pension recipients — both surfaced as flags, the bursar decides.
 *
 * The settlement nets PAYMENTS (severance + outstanding pay + any
 * extra earnings such as accrued leave, pre-filled from the leave
 * balance) against RECOVERIES (loan balances, rent, etc. + any extra
 * the bursar adds). Outstanding pay is payslip-aware: if this month's
 * salary has already been run, it defaults to nil.
 *
 * Earnings and recoveries are both freely add/removable so the bursar
 * can model any real-world exit. The "Download statement" action emits
 * an A4 PDF in the payslip house style.
 * ---------------------------------------------------------------- */
import { useEffect, useMemo, useState } from 'react';
import {
  UserMinus, AlertTriangle, Award, Banknote, ArrowDownCircle, FileDown, Plus, Trash2,
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { GradientHero, SoftCard, StatChip, AccentButton, glassFieldClass } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import { SCHOOL } from '@/lib/school.config';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { useHrData } from '@/features/hr/useHrData';
import StaffPicker from '@/features/hr/StaffPicker';
import { staffFullName, STAFF_CATEGORY_LABELS, END_OF_SERVICE_BENEFIT_LABELS } from '@/lib/hr';
import { computeLeaveBalances } from '@/lib/hrLeave';
import {
  computeSeverance, dailyRate, fmtPula, round2,
  deductionMonthlyAmount, deductionCurrentBalance,
  DEFAULT_DAYS_PER_MONTH, MONTH_LABELS,
  contractGratuityBasePaid, gratuityAccruedOn, resolveExitBenefit,
} from '@/lib/hrPayroll';
import { salesFor } from '@/lib/leaveEncashment';

const REASONS = ['Resignation', 'Termination', 'Redundancy', 'End of contract', 'Other'] as const;

interface Line { id: string; label: string; amount: number; }
interface Recovery { include: boolean; amount: number; }

const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

function completedMonths(start: string, end: string): number {
  const s = new Date(start); const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  let m = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  if (e.getDate() < s.getDate()) m -= 1;
  return Math.max(0, m);
}
/** Weekdays (Mon–Fri) from the 1st of the month up to and including `d`. */
function weekdaysUpTo(d: string): number {
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return 0;
  const y = date.getFullYear(); const mo = date.getMonth(); const day = date.getDate();
  let n = 0;
  for (let i = 1; i <= day; i++) { const wd = new Date(y, mo, i).getDay(); if (wd >= 1 && wd <= 5) n += 1; }
  return n;
}

export default function Severance() {
  const data = useHrData();
  const today = new Date().toISOString().slice(0, 10);

  const [staffId, setStaffId] = useState('');
  const [lastDay, setLastDay] = useState(today);
  const [daysPerMonth, setDaysPerMonth] = useState(DEFAULT_DAYS_PER_MONTH);
  const [reason, setReason] = useState<string>(REASONS[0]);
  const [includeSeverance, setIncludeSeverance] = useState(true);
  const [outstandingDays, setOutstandingDays] = useState(0);
  const [extraEarnings, setExtraEarnings] = useState<Line[]>([]);
  const [recovery, setRecovery] = useState<Record<string, Recovery>>({});
  const [extraRecoveries, setExtraRecoveries] = useState<Line[]>([]);
  const [downloading, setDownloading] = useState(false);

  const staff = data.staff.find((s) => s.id === staffId);
  const profile = data.payrollProfiles.find((p) => p.staffId === staffId);
  const basic = profile?.basicSalary ?? 0;

  // Which exit benefit this contract pays (explicit field wins; else inferred).
  const activeContract = useMemo(() => {
    const cs = data.contracts.filter((c) => c.staffId === staffId);
    return cs.find((c) => c.active) ?? cs[0];
  }, [data.contracts, staffId]);
  const benefitExplicit = !!activeContract?.endOfServiceBenefit;
  const resolvedBenefit = resolveExitBenefit(activeContract?.endOfServiceBenefit, profile?.gratuityEligible ?? false);
  // Contractual gratuity to date — the alternative to statutory severance.
  const myPayslips = useMemo(() => data.payslips.filter((p) => p.staffId === staffId), [data.payslips, staffId]);
  const gratuityBase = contractGratuityBasePaid({
    openingBasicPaid: profile?.openingBasicPaidThisContract,
    contractStartISO: activeContract?.startDate,
    savedPayslips: myPayslips,
    currentPeriod: { month: new Date(lastDay).getMonth() + 1, year: new Date(lastDay).getFullYear() },
    currentBasic: 0,
    payouts: data.gratuityPayouts.filter((p) => p.staffId === staffId && (!activeContract?.id || p.contractId === activeContract.id)),
  });
  const gratuityGross = round2(gratuityAccruedOn(gratuityBase, profile?.gratuityRatePct ?? 0));
  const staffDeductions = useMemo(
    () => data.deductions.filter((d) => d.staffId === staffId && d.status === 'active' && d.type !== 'paye'),
    [data.deductions, staffId],
  );

  // Has this month's salary already been run? Then outstanding pay is nil.
  const finalMonthIdx = new Date(lastDay).getMonth();
  const finalMonthLabel = MONTH_LABELS[finalMonthIdx] ?? '';
  const paidThisMonth = useMemo(() => {
    if (!staffId) return false;
    const d = new Date(lastDay); const y = d.getFullYear(); const m = d.getMonth() + 1;
    return data.payslips.some((p) => p.staffId === staffId && p.period.year === y && p.period.month === m);
  }, [data.payslips, staffId, lastDay]);

  // Seed recoveries + accrued-leave earning whenever the employee changes.
  useEffect(() => {
    if (!staffId) { setRecovery({}); setExtraEarnings([]); setExtraRecoveries([]); return; }
    const prof = data.payrollProfiles.find((p) => p.staffId === staffId);
    const dr = dailyRate(prof?.basicSalary ?? 0, daysPerMonth);
    const init: Record<string, Recovery> = {};
    for (const d of data.deductions.filter((x) => x.staffId === staffId && x.status === 'active' && x.type !== 'paye')) {
      // LIVE outstanding balance — recovering the stored (original) amount
      // would over-claw a loan the employee has been repaying monthly.
      const bal = deductionCurrentBalance(d);
      init[d.id] = bal != null && bal > 0 ? { include: true, amount: round2(bal) } : { include: false, amount: round2(deductionMonthlyAmount(d)) };
    }
    setRecovery(init);
    setExtraRecoveries([]);
    // Pre-check severance only when the contract pays statutory severance.
    const cs = data.contracts.filter((c) => c.staffId === staffId);
    const ac = cs.find((c) => c.active) ?? cs[0];
    setIncludeSeverance(resolveExitBenefit(ac?.endOfServiceBenefit, prof?.gratuityEligible ?? false) === 'severance');
    const bals = computeLeaveBalances(
      data.leaveAllocations.find((a) => a.staffId === staffId),
      data.leave.filter((l) => l.staffId === staffId),
      /* Days already sold are not owed again on exit. Leaving them in pays
         the same leave twice - once when sold, once in the settlement. */
      { asOf: new Date(lastDay), accrualStart: data.payrollSettings?.leaveAccrualStart, employmentStart: staff?.startDate, category: staff?.category, encashments: salesFor(data.leaveEncashments, staffId) },
    );
    const annualDays = Math.max(0, bals.find((b) => b.type === 'annual')?.remaining ?? 0);
    setExtraEarnings(annualDays > 0
      ? [{ id: newId(), label: `Accrued leave (${annualDays} day${annualDays === 1 ? '' : 's'})`, amount: round2(dr * annualDays) }]
      : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId]);

  // Outstanding-pay default tracks the employee and the leaving month.
  useEffect(() => {
    if (!staffId) { setOutstandingDays(0); return; }
    const d = new Date(lastDay); const y = d.getFullYear(); const m = d.getMonth() + 1;
    const paid = data.payslips.some((p) => p.staffId === staffId && p.period.year === y && p.period.month === m);
    setOutstandingDays(paid ? 0 : weekdaysUpTo(lastDay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId, lastDay]);

  const months = staff ? completedMonths(staff.startDate, lastDay) : 0;
  const sev = computeSeverance(basic, months, daysPerMonth);
  const daily = dailyRate(basic, daysPerMonth);
  const outstandingPay = round2(daily * Math.max(0, outstandingDays));
  const extraEarnTotal = round2(extraEarnings.reduce((a, e) => a + (Number(e.amount) || 0), 0));
  const grossPayable = round2((includeSeverance ? sev.amount : 0) + outstandingPay + extraEarnTotal);

  const knownRecovered = staffDeductions.reduce((a, d) => a + (recovery[d.id]?.include ? (recovery[d.id]?.amount ?? 0) : 0), 0);
  const extraRecovered = extraRecoveries.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const totalRecovered = round2(knownRecovered + extraRecovered);
  const net = round2(grossPayable - totalRecovered);

  const handleDownload = async () => {
    if (!staff || basic <= 0) return;
    setDownloading(true);
    try {
      const logo = await loadLogoDataUrl();
      const flags: string[] = [];
      if (includeSeverance && months < 60) flags.push(`Below the statutory 60-month threshold (${months} month${months === 1 ? '' : 's'}) — severance is not automatically due.`);
      if (resolvedBenefit === 'gratuity') flags.push(`Contract pays contractual gratuity${benefitExplicit ? '' : ' (inferred)'} — statutory severance is not payable to gratuity recipients${includeSeverance ? '; severance is currently included — confirm this is intended.' : '.'}`);
      if (resolvedBenefit === 'none') flags.push('Contract specifies no end-of-service benefit — confirm before paying.');

      const earnings: Line[] = [
        ...(includeSeverance ? [{ id: 'sev', label: `Severance — ${sev.daysAccrued} day${sev.daysAccrued === 1 ? '' : 's'} @ ${fmtPula(daily)}/day`, amount: sev.amount }] : []),
        { id: 'out', label: `Outstanding pay — ${outstandingDays} day${outstandingDays === 1 ? '' : 's'} @ ${fmtPula(daily)}/day`, amount: outstandingPay },
        ...extraEarnings
          .filter((e) => (Number(e.amount) || 0) !== 0 || e.label.trim())
          .map((e) => ({ id: e.id, label: e.label.trim() || 'Earning', amount: round2(Number(e.amount) || 0) })),
      ];
      const recoveries: Line[] = [
        ...staffDeductions.filter((d) => recovery[d.id]?.include).map((d) => ({ id: d.id, label: d.label, amount: round2(recovery[d.id]?.amount ?? 0) })),
        ...extraRecoveries
          .filter((r) => (Number(r.amount) || 0) !== 0 || r.label.trim())
          .map((r) => ({ id: r.id, label: r.label.trim() || 'Deduction', amount: round2(Number(r.amount) || 0) })),
      ];

      const pdf = buildSettlementPdf({
        staffName: staffFullName(staff),
        employeeNo: staff.employeeNo,
        category: STAFF_CATEGORY_LABELS[staff.category],
        startDate: (staff.startDate || '').slice(0, 10),
        lastDay, months, reason,
        earnings, grossPayable, recoveries, totalRecovered, net, flags, logo,
      });
      pdf.save(`final_settlement_${staff.employeeNo}_${lastDay}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading payroll…" /></div>;
  }

  return (
    <div className="space-y-6">
      <GradientHero
        icon={UserMinus}
        title="Severance & Final Settlement"
        subtitle="Statutory severance, outstanding pay, and amounts owed — netted into a final payout"
        actions={staff && basic > 0 ? (
          <AccentButton icon={FileDown} onClick={handleDownload} disabled={downloading}>
            {downloading ? 'Preparing…' : 'Download statement'}
          </AccentButton>
        ) : undefined}
      />

      {/* Employee + parameters */}
      <SoftCard hover={false}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-4">
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Employee</label>
            <StaffPicker staff={data.staff} value={staffId} onChange={setStaffId} />
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Last working day</span>
                <input type="date" value={lastDay} onChange={(e) => setLastDay(e.target.value)} className={glassFieldClass('py-2')} />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Days / month (daily-rate basis)</span>
                <select value={daysPerMonth} onChange={(e) => setDaysPerMonth(Number(e.target.value))} className={glassFieldClass('py-2')}>
                  <option value={22}>22 (5-day week)</option>
                  <option value={24}>24 (5½-day week)</option>
                  <option value={26}>26 (6-day week)</option>
                </select>
              </label>
            </div>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Reason for leaving</span>
              <select value={reason} onChange={(e) => setReason(e.target.value)} className={glassFieldClass('py-2')}>
                {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
          </div>
        </div>

        {staff && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><span className="text-gray-400 block text-xs">Employee</span>{staffFullName(staff)} <span className="text-gray-400">#{staff.employeeNo}</span></div>
            <div><span className="text-gray-400 block text-xs">Start date</span>{(staff.startDate || '').slice(0, 10) || '—'}</div>
            <div><span className="text-gray-400 block text-xs">Service</span>{months} month{months === 1 ? '' : 's'}</div>
            <div><span className="text-gray-400 block text-xs">Basic salary</span>{basic > 0 ? fmtPula(basic) : <span className="text-amber-500">not set</span>}</div>
          </div>
        )}
      </SoftCard>

      {!staff ? (
        <SoftCard hover={false}><p className="text-sm text-gray-500 dark:text-gray-400">Search for an employee to calculate their final settlement.</p></SoftCard>
      ) : basic <= 0 ? (
        <SoftCard hover={false}>
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
            <AlertTriangle size={16} /> No basic salary on file for {staffFullName(staff)} — set it on their Payroll tab to calculate severance.
          </div>
        </SoftCard>
      ) : (
        <>
          {/* End-of-service benefit — per contract */}
          <SoftCard hover={false}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm flex items-center gap-2">
                <Award size={15} /> End-of-service benefit
              </h3>
              <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${
                resolvedBenefit === 'gratuity' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                : resolvedBenefit === 'severance' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {END_OF_SERVICE_BENEFIT_LABELS[resolvedBenefit]}{benefitExplicit ? '' : ' (inferred)'}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className={`rounded-xl px-3 py-2.5 ${resolvedBenefit === 'severance' ? 'ring-1 ring-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/40'}`}>
                <span className="text-[11px] text-gray-500 dark:text-gray-400 block">Statutory severance ({months} mo)</span>
                <span className="font-semibold text-gray-800 dark:text-gray-100">{fmtPula(sev.amount)}</span>
              </div>
              <div className={`rounded-xl px-3 py-2.5 ${resolvedBenefit === 'gratuity' ? 'ring-1 ring-violet-300 bg-violet-50/60 dark:bg-violet-900/20' : 'bg-gray-50 dark:bg-gray-700/40'}`}>
                <span className="text-[11px] text-gray-500 dark:text-gray-400 block">Contractual gratuity{profile?.gratuityRatePct ? ` (${profile.gratuityRatePct}%)` : ''}</span>
                <span className="font-semibold text-gray-800 dark:text-gray-100">{profile?.gratuityEligible ? fmtPula(gratuityGross) : '—'}</span>
              </div>
            </div>
            <div className="space-y-1.5 mt-3 text-sm">
              {!benefitExplicit && (
                <p className="flex items-start gap-2 text-gray-500 dark:text-gray-400">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  No benefit set on the contract — inferred from the gratuity flag. Set it on the Contract tab to make it explicit.
                </p>
              )}
              {resolvedBenefit === 'gratuity' && (
                <p className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  This contract pays gratuity (settled via the final payslip). Statutory severance isn't payable to gratuity recipients — leave severance unchecked unless school policy says otherwise.
                </p>
              )}
              {includeSeverance && months < 60 && (
                <p className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  Below the statutory 60-month threshold ({months} months). Severance isn't automatically due — pay only if the contract or school policy provides for it.
                </p>
              )}
              {resolvedBenefit === 'none' && (
                <p className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  This contract specifies no end-of-service benefit — confirm before paying anything.
                </p>
              )}
            </div>
          </SoftCard>

          {/* Payments */}
          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
              <Banknote size={16} className="text-[var(--accent-link,#2d5295)]" />
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Payments due</h3>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={includeSeverance} onChange={(e) => setIncludeSeverance(e.target.checked)} className="rounded" />
                  <span className="flex items-center gap-1.5"><Award size={14} className="text-gray-400" /> Severance
                    <span className="text-gray-400">— {sev.daysAccrued} days × {fmtPula(daily)}/day</span>
                  </span>
                </label>
                <span className="font-semibold text-gray-800 dark:text-gray-100">{fmtPula(includeSeverance ? sev.amount : 0)}</span>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span>Outstanding pay</span>
                    <input type="number" value={outstandingDays === 0 ? '' : outstandingDays} onChange={(e) => setOutstandingDays(Number(e.target.value) || 0)} className={glassFieldClass('py-1 text-sm w-20')} />
                    <span className="text-gray-400">days × {fmtPula(daily)}/day</span>
                  </div>
                  <span className="font-semibold text-gray-800 dark:text-gray-100">{fmtPula(outstandingPay)}</span>
                </div>
                <p className="text-xs text-gray-400 mt-1 ml-[1px]">
                  {paidThisMonth
                    ? `${finalMonthLabel} salary already run (payslip on file) — defaulted to nil.`
                    : `${finalMonthLabel} salary not yet run — defaulted to ${weekdaysUpTo(lastDay)} working days to the last day.`}
                </p>
              </div>

              {extraEarnings.map((e) => (
                <div key={e.id} className="flex items-center gap-2">
                  <input value={e.label} onChange={(ev) => setExtraEarnings((rows) => rows.map((r) => r.id === e.id ? { ...r, label: ev.target.value } : r))} placeholder="Earning (e.g. accrued leave, bonus)" className={glassFieldClass('py-1 text-sm flex-1')} />
                  <input type="number" value={e.amount === 0 ? '' : e.amount} onChange={(ev) => setExtraEarnings((rows) => rows.map((r) => r.id === e.id ? { ...r, amount: Number(ev.target.value) || 0 } : r))} placeholder="0.00" className={glassFieldClass('py-1 text-sm w-28 text-right')} />
                  <button onClick={() => setExtraEarnings((rows) => rows.filter((r) => r.id !== e.id))} className="p-1.5 text-gray-400 hover:text-red-500" title="Remove"><Trash2 size={15} /></button>
                </div>
              ))}

              <button onClick={() => setExtraEarnings((rows) => [...rows, { id: newId(), label: '', amount: 0 }])} className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline">
                <Plus size={14} /> Add earning
              </button>

              <div className="flex items-center justify-between gap-3 border-t border-gray-100 dark:border-gray-700 pt-2 font-semibold text-gray-800 dark:text-gray-100">
                <span>Gross payable</span><span>{fmtPula(grossPayable)}</span>
              </div>
            </div>
          </SoftCard>

          {/* Recoveries */}
          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
              <ArrowDownCircle size={16} className="text-red-500" />
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Amounts owed to the school (recovered)</h3>
            </div>
            <div className="p-5 space-y-3">
              {staffDeductions.length === 0 && extraRecoveries.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No active deductions on file — add any amounts to recover below.</p>
              ) : (
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-sm">
                    {staffDeductions.length > 0 && (
                      <thead>
                        <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                          <th className="py-2 pr-2 w-8"></th>
                          <th className="py-2 px-3 font-medium">Deduction</th>
                          <th className="py-2 px-3 font-medium text-right">Monthly</th>
                          <th className="py-2 px-3 font-medium text-right">Balance</th>
                          <th className="py-2 px-3 font-medium text-right">Recover</th>
                          <th className="py-2 w-8"></th>
                        </tr>
                      </thead>
                    )}
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                      {staffDeductions.map((d) => {
                        const bal = deductionCurrentBalance(d);
                        const r = recovery[d.id] ?? { include: false, amount: 0 };
                        return (
                          <tr key={d.id} className={r.include ? '' : 'opacity-60'}>
                            <td className="py-2 pr-2">
                              <input type="checkbox" checked={r.include} onChange={(e) => setRecovery((m) => ({ ...m, [d.id]: { ...r, include: e.target.checked } }))} className="rounded" />
                            </td>
                            <td className="py-2 px-3 text-gray-800 dark:text-gray-100">{d.label}</td>
                            <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">{fmtPula(deductionMonthlyAmount(d))}</td>
                            <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">{bal != null ? fmtPula(bal) : '—'}</td>
                            <td className="py-2 px-3 text-right">
                              <input type="number" value={r.amount === 0 ? '' : r.amount} onChange={(e) => setRecovery((m) => ({ ...m, [d.id]: { ...r, amount: Number(e.target.value) || 0 } }))} className={glassFieldClass('py-1 text-sm w-28 text-right')} />
                            </td>
                            <td className="py-2"></td>
                          </tr>
                        );
                      })}
                      {extraRecoveries.map((r) => (
                        <tr key={r.id}>
                          <td className="py-2 pr-2"></td>
                          <td className="py-2 px-3" colSpan={3}>
                            <input value={r.label} onChange={(ev) => setExtraRecoveries((rows) => rows.map((x) => x.id === r.id ? { ...x, label: ev.target.value } : x))} placeholder="Other amount owed (e.g. asset, advance)" className={glassFieldClass('py-1 text-sm w-full')} />
                          </td>
                          <td className="py-2 px-3 text-right">
                            <input type="number" value={r.amount === 0 ? '' : r.amount} onChange={(ev) => setExtraRecoveries((rows) => rows.map((x) => x.id === r.id ? { ...x, amount: Number(ev.target.value) || 0 } : x))} placeholder="0.00" className={glassFieldClass('py-1 text-sm w-28 text-right')} />
                          </td>
                          <td className="py-2 pl-1">
                            <button onClick={() => setExtraRecoveries((rows) => rows.filter((x) => x.id !== r.id))} className="p-1.5 text-gray-400 hover:text-red-500" title="Remove"><Trash2 size={15} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center justify-between">
                <button onClick={() => setExtraRecoveries((rows) => [...rows, { id: newId(), label: '', amount: 0 }])} className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline">
                  <Plus size={14} /> Add deduction
                </button>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Total recovered: <span className="text-red-600 dark:text-red-400">−{fmtPula(totalRecovered)}</span></span>
              </div>
            </div>
          </SoftCard>

          {/* Net settlement */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatChip tone="blue" value={fmtPula(grossPayable)} label="Gross payable" />
            <StatChip tone="red" value={`−${fmtPula(totalRecovered)}`} label="Recovered" />
            <StatChip tone="emerald" value={fmtPula(net)} label="Net final settlement" />
          </div>

          <p className="text-xs text-gray-400 dark:text-gray-500">
            A calculation aid. On termination, Botswana law requires all dues to be settled in the final pay cycle (or as soon as practicable). Confirm eligibility and figures against the contract and current legislation.
          </p>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------
 * Settlement statement PDF (jsPDF). Kept in-file because this is a
 * rare, one-off document rather than a daily-use renderer. Mirrors
 * the payslip's A4 / navy house style.
 * ---------------------------------------------------------------- */
interface SettlementPdfInput {
  staffName: string; employeeNo: string; category: string;
  startDate: string; lastDay: string; months: number; reason: string;
  earnings: Line[];
  grossPayable: number;
  recoveries: Line[];
  totalRecovered: number;
  net: number;
  flags: string[];
  logo: string | null;
}

function buildSettlementPdf(s: SettlementPdfInput): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 16; const right = 210 - MX;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  const money = (n: number) => fmtPula(n);
  let y = 16;

  if (s.logo) { try { pdf.addImage(s.logo, 'PNG', MX, y - 2, 18, 18); } catch { /* ignore */ } }
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text(SCHOOL.name, MX + 22, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}  ·  ${SCHOOL.contact.website}`, MX + 22, y + 9);
  y += 22;
  setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 8;

  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
  pdf.text('FINAL SETTLEMENT STATEMENT', MX, y); y += 6;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(`Issued ${new Date().toISOString().slice(0, 10)}`, MX, y); y += 9;

  const rowH = 9;
  const detail = (label: string, value: string, col: 0 | 1, rowIdx: number) => {
    const x = col === 0 ? MX : MX + 92;
    const yy = y + rowIdx * rowH;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); setT(MUTED);
    pdf.text(label, x, yy);
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9.5); setT(INK);
    pdf.text(value || '—', x, yy + 4.5);
  };
  detail('EMPLOYEE', `${s.staffName}  (#${s.employeeNo})`, 0, 0);
  detail('CATEGORY', s.category, 1, 0);
  detail('START DATE', s.startDate, 0, 1);
  detail('LAST WORKING DAY', s.lastDay, 1, 1);
  detail('SERVICE', `${s.months} month${s.months === 1 ? '' : 's'}`, 0, 2);
  detail('REASON', s.reason, 1, 2);
  y += 3 * rowH + 4;

  const sectionHeader = (t: string) => {
    setF(NAVY); pdf.rect(MX, y, right - MX, 7, 'F');
    pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
    pdf.text(t, MX + 2, y + 4.8); y += 7 + 3;
  };
  const lineItem = (label: string, amount: number, bold = false) => {
    setT(INK); pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(9);
    pdf.text(label, MX + 2, y);
    pdf.text(money(amount), right - 2, y, { align: 'right' });
    y += 6;
  };
  const ruleAbove = () => { setD(RULE); pdf.setLineWidth(0.3); pdf.line(MX, y - 4, right, y - 4); };

  sectionHeader('PAYMENTS DUE');
  s.earnings.forEach((e) => lineItem(e.label, e.amount));
  ruleAbove(); lineItem('Gross payable', s.grossPayable, true); y += 3;

  sectionHeader('AMOUNTS OWED TO THE SCHOOL (RECOVERED)');
  if (s.recoveries.length === 0) {
    setT(MUTED); pdf.setFont('helvetica', 'italic'); pdf.setFontSize(9);
    pdf.text('None.', MX + 2, y); y += 6;
  } else {
    s.recoveries.forEach((r) => lineItem(r.label, -r.amount));
  }
  ruleAbove(); lineItem('Total recovered', -s.totalRecovered, true); y += 4;

  setF([240, 244, 250]); setD(NAVY); pdf.setLineWidth(0.5);
  pdf.rect(MX, y, right - MX, 12, 'FD');
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
  pdf.text('NET FINAL SETTLEMENT', MX + 3, y + 7.8);
  pdf.text(money(s.net), right - 3, y + 7.8, { align: 'right' });
  y += 18;

  if (s.flags.length) {
    setT(MUTED); pdf.setFont('helvetica', 'italic'); pdf.setFontSize(8);
    s.flags.forEach((f) => {
      const lines = pdf.splitTextToSize(`• ${f}`, right - MX) as string[];
      pdf.text(lines, MX, y); y += lines.length * 4 + 1;
    });
    y += 2;
  }

  setT(MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7);
  const disc = pdf.splitTextToSize(
    'This statement is a calculation aid prepared by the School. Final dues are settled in accordance with the Botswana Employment Act and the employee\u2019s contract of employment. Acepsle Pty Ltd makes no representation as to the accuracy of statutory figures.',
    right - MX,
  ) as string[];
  pdf.text(disc, MX, y); y += disc.length * 3.4 + 12;

  const sigW = (right - MX - 10) / 2;
  setD(INK); pdf.setLineWidth(0.3);
  pdf.line(MX, y, MX + sigW, y);
  pdf.line(MX + sigW + 10, y, right, y);
  pdf.setFontSize(8); setT(MUTED);
  pdf.text('Employee signature & date', MX, y + 4);
  pdf.text('For the School (HR / Bursar) & date', MX + sigW + 10, y + 4);

  return pdf;
}
